
import { useState, useEffect, useCallback, MouseEvent, ChangeEvent } from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import Color from '@tiptap/extension-color';
import TextStyle from '@tiptap/extension-text-style';
import { Button } from '@/components/ui/button';
import { Bold, Italic, Underline as UnderlineIcon, AlignLeft, AlignCenter,
  AlignRight, Link as LinkIcon, Type, ListOrdered, List, Unlink } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import FontSize from './extensions/FontSize';

interface WysiwygEditorProps {
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  editorProps?: Record<string, any>;
}

const WysiwygEditor: React.FC<WysiwygEditorProps> = ({
  value,
  onChange,
  disabled = false,
  editorProps = {}
}) => {
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [fontSizeOpen, setFontSizeOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [customColor, setCustomColor] = useState('#000000');
  
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        bulletList: {},
        orderedList: {}
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-500 underline'
        }
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph']
      }),
      TextStyle,
      Color,
      FontSize
    ],
    content: value,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editable: !disabled,
    ...editorProps
  });

  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value);
    }
  }, [editor, value]);

  const handleColorSelect = useCallback((color: string) => {
    if (editor) {
      editor.chain().focus().setColor(color).run();
      setColorPickerOpen(false);
    }
  }, [editor]);

  const handleFontSize = useCallback((size: number) => {
    editor?.chain().focus().setFontSize(`${size}px`).run();
    setFontSizeOpen(false);
  }, [editor]);

  const handleInsertLink = useCallback(() => {
    if (linkUrl) {
      editor?.chain().focus().extendMarkRange('link').setLink({
        href: linkUrl
      }).run();
    } else {
      editor?.chain().focus().unsetLink().run();
    }
    setLinkUrl('');
    setLinkOpen(false);
  }, [editor, linkUrl]);

  const handleRemoveLink = useCallback(() => {
    editor?.chain().focus().unsetLink().run();
    setLinkUrl('');
    setLinkOpen(false);
  }, [editor]);

  const handleLinkOpenChange = useCallback((open: boolean) => {
    if (open && editor) {
      // Get existing link URL without focusing (to avoid triggering onInteractOutside)
      const existingUrl = editor.getAttributes('link').href || '';
      setLinkUrl(existingUrl);
    }
    setLinkOpen(open);
  }, [editor]);

  const handleColorClick = useCallback((e: MouseEvent, color: string) => {
    e.preventDefault();
    e.stopPropagation();
    handleColorSelect(color);
  }, [handleColorSelect]);

  const handleCustomColorChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setCustomColor(e.target.value);
  }, []);

  const handleCustomColorApply = useCallback((e: MouseEvent | React.KeyboardEvent) => {
    e.preventDefault();
    e.stopPropagation();
    handleColorSelect(customColor);
  }, [customColor, handleColorSelect]);
  if (!editor) {
    return null;
  }

  const toggleBulletList = () => {
    editor.chain().focus().toggleBulletList().run();
  };

  const toggleOrderedList = () => {
    editor.chain().focus().toggleOrderedList().run();
  };

  const colorOptions = ['#000000', '#434343', '#666666', '#999999', '#b7b7b7', '#cccccc', '#d9d9d9', '#efefef', '#f3f3f3', '#ffffff', '#980000', '#ff0000', '#ff9900', '#ffff00', '#00ff00', '#00ffff', '#4a86e8', '#0000ff', '#9900ff', '#ff00ff', '#e6b8af', '#f4cccc', '#fce5cd', '#fff2cc', '#d9ead3', '#d0e0e3', '#c9daf8', '#cfe2f3', '#d9d2e9', '#ead1dc'];
  const fontSizes = [8, 10, 12, 14, 16, 18, 20, 24, 30, 36];

  return <div className="border border-gray-300 rounded-md flex flex-col">
      <div className="flex flex-wrap gap-1 p-2 border-b border-gray-200 bg-gray-50">
        <Button type="button" variant="ghost" size="sm" className={`h-8 px-2 ${editor.isActive('bold') ? 'bg-accent' : ''}`} onClick={() => editor.chain().focus().toggleBold().run()} disabled={!editor.can().chain().focus().toggleBold().run()}>
          <Bold className="h-4 w-4" />
        </Button>

        <Button type="button" variant="ghost" size="sm" className={`h-8 px-2 ${editor.isActive('italic') ? 'bg-accent' : ''}`} onClick={() => editor.chain().focus().toggleItalic().run()} disabled={!editor.can().chain().focus().toggleItalic().run()}>
          <Italic className="h-4 w-4" />
        </Button>

        <Button type="button" variant="ghost" size="sm" className={`h-8 px-2 ${editor.isActive('underline') ? 'bg-accent' : ''}`} onClick={() => editor.chain().focus().toggleUnderline().run()} disabled={!editor.can().chain().focus().toggleUnderline().run()}>
          <UnderlineIcon className="h-4 w-4" />
        </Button>

        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          className={`h-8 px-2 ${editor.isActive('bulletList') ? 'bg-accent' : ''}`} 
          onClick={toggleBulletList}
          disabled={disabled}
        >
          <List className="h-4 w-4" />
        </Button>

        <Button 
          type="button" 
          variant="ghost" 
          size="sm" 
          className={`h-8 px-2 ${editor.isActive('orderedList') ? 'bg-accent' : ''}`} 
          onClick={toggleOrderedList}
          disabled={disabled}
        >
          <ListOrdered className="h-4 w-4" />
        </Button>

        <Button type="button" variant="ghost" size="sm" className={`h-8 px-2 ${editor.isActive({
        textAlign: 'left'
      }) ? 'bg-accent' : ''}`} onClick={() => editor.chain().focus().setTextAlign('left').run()}>
          <AlignLeft className="h-4 w-4" />
        </Button>

        <Button type="button" variant="ghost" size="sm" className={`h-8 px-2 ${editor.isActive({
        textAlign: 'center'
      }) ? 'bg-accent' : ''}`} onClick={() => editor.chain().focus().setTextAlign('center').run()}>
          <AlignCenter className="h-4 w-4" />
        </Button>

        <Button type="button" variant="ghost" size="sm" className={`h-8 px-2 ${editor.isActive({
        textAlign: 'right'
      }) ? 'bg-accent' : ''}`} onClick={() => editor.chain().focus().setTextAlign('right').run()}>
          <AlignRight className="h-4 w-4" />
        </Button>

        <Popover open={colorPickerOpen} onOpenChange={setColorPickerOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-8 px-2" disabled={disabled}>
              <div className="flex items-center">
                <div className="w-4 h-4 rounded-full border border-gray-300" style={{
                backgroundColor: editor.getAttributes('textStyle').color || '#000000'
              }} />
              </div>
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-2" align="start" sideOffset={5} onInteractOutside={() => setColorPickerOpen(false)} onEscapeKeyDown={() => setColorPickerOpen(false)}>
            <div className="grid grid-cols-10 gap-1 mb-2">
              {colorOptions.map(color => <button key={color} type="button" className="w-5 h-5 rounded-full border border-gray-300 hover:scale-125 transition-transform cursor-pointer" style={{
              backgroundColor: color
            }} onClick={e => handleColorClick(e, color)} tabIndex={colorPickerOpen ? 0 : -1} />)}
            </div>
            
            <div className="mt-3 border-t pt-2">
              <label htmlFor="custom-color" className="text-sm font-medium">
                Advanced Color Picker
              </label>
              <div className="flex items-center mt-1">
                <input type="color" id="custom-color" className="w-10 h-10 p-0 border rounded cursor-pointer" value={customColor} onChange={handleCustomColorChange} tabIndex={colorPickerOpen ? 0 : -1} />
                <input type="text" value={customColor} className="ml-2 flex-1 h-8 px-2 border rounded" onChange={e => {
                setCustomColor(e.target.value);
              }} tabIndex={colorPickerOpen ? 0 : -1} />
                <Button type="button" size="sm" className="ml-2 h-8" onClick={handleCustomColorApply} tabIndex={colorPickerOpen ? 0 : -1}>
                  Apply
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>

        <Popover open={fontSizeOpen} onOpenChange={setFontSizeOpen}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className="h-8 px-2" disabled={disabled}>
              <Type className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-32" align="start" sideOffset={5} onInteractOutside={() => setFontSizeOpen(false)} onEscapeKeyDown={() => setFontSizeOpen(false)}>
            <div className="grid grid-cols-3 gap-1">
              {fontSizes.map(size => <Button key={size} type="button" variant="outline" size="sm" onClick={e => {
              handleFontSize(size);
            }} className="h-7 text-xs px-1" tabIndex={fontSizeOpen ? 0 : -1}>
                  {size}
                </Button>)}
            </div>
          </PopoverContent>
        </Popover>

        <Popover open={linkOpen} onOpenChange={handleLinkOpenChange}>
          <PopoverTrigger asChild>
            <Button type="button" variant="ghost" size="sm" className={`h-8 px-2 ${editor.isActive('link') ? 'bg-accent' : ''}`} disabled={disabled}>
              <LinkIcon className="h-4 w-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80 p-4" align="end" side="bottom" sideOffset={5} onInteractOutside={() => setLinkOpen(false)} onEscapeKeyDown={() => setLinkOpen(false)}>
            <div className="flex flex-col gap-4">
              <div className="grid w-full items-center gap-2">
                <label htmlFor="url" className="text-sm font-medium">
                  {editor.isActive('link') ? 'Edit Link URL' : 'Add Link URL'}
                </label>
                <input id="url" type="url" className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm" placeholder="https://example.com" value={linkUrl} onChange={e => {
                setLinkUrl(e.target.value);
              }} onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleInsertLink();
                }
              }} autoFocus tabIndex={linkOpen ? 0 : -1} />
              </div>
              <div className="flex justify-between">
                <div className="flex gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => {
                    setLinkUrl('');
                    setLinkOpen(false);
                  }} tabIndex={linkOpen ? 0 : -1}>
                    Cancel
                  </Button>
                  {editor.isActive('link') && (
                    <Button type="button" variant="destructive" size="sm" onClick={handleRemoveLink} tabIndex={linkOpen ? 0 : -1}>
                      <Unlink className="h-4 w-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </div>
                <Button type="button" size="sm" onClick={handleInsertLink} tabIndex={linkOpen ? 0 : -1}>
                  {editor.isActive('link') ? 'Update' : 'Save'}
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      
      <div className="flex-grow min-h-[150px] h-[150px]">
          <EditorContent 
            editor={editor} 
            className="prose prose-sm max-w-none pl-4 pr-4 focus:outline-none tiptap-editor-content h-full"
          />
      </div>
    </div>;
};

export default WysiwygEditor;
